<script setup lang="ts">
import { useVueI18n } from '../../locales'
import { debounce } from 'lodash'
import { NoPreviewAvailable } from '../../assets'
import {
  UIInput,
  UISpace,
  UISkeleton,
  UITextMdNormal,
} from '@gohighlevel/ghl-ui'
import { onMounted, onUnmounted, ref } from 'vue'
import { useContentLibraryFactory } from '../../composition/useContentLibrary'
import { useRoute } from 'vue-router'
import { ContentLibraryEvents } from '../../types/components/content-library.schema'
import { DragHandleSVG } from '../../assets'
const t = useVueI18n()

const route = useRoute()
const searchName = ref('')
const {
  totalDocuments,
  getCustomContentLibPaginated,
  fetchContentLibraryLoading,
  clData,
} = useContentLibraryFactory(route)
const emits = defineEmits([
  ContentLibraryEvents.ON_DRAG_START,
  ContentLibraryEvents.ON_DRAG_END,
])

const triggerScrollBottom = (value: boolean) => {
  if (!value) return
  if (totalDocuments.value === clData.value.length) return
  if (totalDocuments.value > clData.value.length) {
    if (!fetchContentLibraryLoading.value) {
      debounce(
        () => getCustomContentLibPaginated(searchName.value, true),
        200
      )()
    }
  }
}

const debouncedTriggerScroll = debounce(() => {
  triggerScrollBottom(true)
}, 300)

const attachScrollEvent = () => {
  setTimeout(function () {
    const element = document.getElementById('content-library-scroll')
    if (element) {
      element.addEventListener('scroll', () => {
        if (
          element.scrollTop + element.clientHeight >=
          element.scrollHeight - 1
        ) {
          debouncedTriggerScroll()
        }
      })
    }
  }, 1000)
}

onMounted(() => {
  getCustomContentLibPaginated(searchName.value)
  attachScrollEvent()
})

onUnmounted(() => {
  const element = document.getElementById('content-library-scroll')
  if (element) {
    element.removeEventListener('scroll', debouncedTriggerScroll)
  }
})

function onEnd() {
  emits(ContentLibraryEvents.ON_DRAG_END)
}

const startDrag = (evt: any, item: any) => {
  emits(ContentLibraryEvents.ON_DRAG_START, evt, item)
}

const debouncedFetch = debounce(() => {
  getCustomContentLibPaginated(searchName.value, false, true)
}, 400)

const onUpdateSearchName = (value: string) => {
  searchName.value = value
  debouncedFetch()
}
</script>

<template>
  <div class="proposal-content-library-custom-scroll">
    <div class="mb-4">
      <UIInput
        :id="'proposals_search'"
        :placeholder="t('proposalsList.search')"
        class="w-full border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        :clearable="true"
        v-model:value="searchName"
        @update:value="value => onUpdateSearchName(value)"
      >
      </UIInput>
    </div>
  </div>
  <div
    class="w-full overflow-y-auto max-h-[inherit]"
    id="view-content-library-area"
  >
    <div
      v-if="clData.length === 0 && !fetchContentLibraryLoading"
      class="flex justify-center items-center"
    >
      <div class="text-center">
        <UITextMdNormal class="text-gray-500">
          {{ t('contentLibrary.noContentFound') }}
        </UITextMdNormal>
      </div>
    </div>
    <div
      v-else-if="clData.length > 0"
      class="grid custom-grid grid-cols-2 gap-4 w-full"
    >
      <div
        v-for="(item, index) in clData"
        :key="index"
        class="border rounded-lg shadow-lg inset-shadow-sm p-2 flex flex-col"
      >
        <div
          class="relative w-full cursor-pointer"
          @dragstart="startDrag($event, item)"
          @dragend="onEnd()"
          draggable="true"
        >
          <img
            v-if="item.contentLibraryThumbnailUrl"
            :src="item.contentLibraryThumbnailUrl"
            alt="thumbnail"
            class="w-full h-48 custom-height object-cover rounded-lg border"
          />
          <div
            v-else
            class="w-full h-48 custom-height flex items-center justify-center bg-gray-200 rounded-lg"
          >
            <NoPreviewAvailable class="w-full custom-height h-48" />
          </div>
          <div class="overlay-group-el flex items-center justify-center handle">
            <DragHandleSVG
              :style="{ transform: 'rotate(90deg)' }"
              class="h-[22px] w-[22px]"
            />
          </div>
        </div>
        <div class="flex flex-row justify-center items-center">
          <div v-if="item.name" class="flex text-sm font-semibold mb-2 mt-2">
            {{ item.name }}
          </div>
        </div>
      </div>
    </div>
    <UISpace
      vertical
      v-if="fetchContentLibraryLoading"
      class="flex flex-cols-2 gap-4 p-2 mt-4"
    >
      <div class="flex flex-cols-2 gap-4">
        <UISkeleton
          class="flex-1 border rounded-lg"
          height="12rem"
          width="45%"
        />
        <UISkeleton
          class="flex-1 border rounded-lg"
          height="12rem"
          width="45%"
        />
      </div>
      <div class="flex flex-cols-2 gap-4">
        <UISkeleton
          class="flex-1 border rounded-lg"
          height="12rem"
          width="45%"
        />
        <UISkeleton
          class="flex-1 border rounded-lg"
          height="12rem"
          width="45%"
        />
      </div>
    </UISpace>
  </div>
</template>

<style lang="scss" scoped>
.spinner {
  animation: spin 1s linear infinite;
}

.loading-overlay {
  background: rgba(255, 255, 255, 0.6);
}

.custom-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
}

.custom-height {
  height: 12rem /* 192px */;
}

.delete-icon {
  background-color: rgba(255, 255, 255, 0.6);
  bottom: 15px;
  right: 15px;
}

.name-block {
  flex-direction: column;
  justify-content: flex-start;
}

.overlay-group-el {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  border-radius: 0.5rem;
}

.overlay-group-el:hover {
  opacity: 0.6;
}

.handle ::v-deep(svg) {
  path {
    stroke: white;
  }
}

.shadow {
  --tw-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.1),
    0 8px 10px -6px rgb(0 0 0 / 0.1);
  --tw-shadow-colored: 0 20px 25px -5px var(--tw-shadow-color),
    0 8px 10px -6px var(--tw-shadow-color);
  box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000),
    var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}
</style>
